import * as React from 'react';
import { Image } from 'antd';
import { useHeicSafeSrc } from '../../hooks/useHeicSafeSrc';

/**
 * WhatsApp-style collage for a note's image(s): count-aware layout (1 / 2 / 3 / 4 / +N),
 * square tiles with rounded corners and small gaps, and a shared antd lightbox you can
 * page through. Mirrors the mobile app's ImageGrid. Photos are full CDN URLs.
 */

const GAP = 3;
const RADIUS = 10;
const MULTI_MAX_WIDTH = 330;
const SINGLE_MAX_WIDTH = 260;

const overlayStyle: React.CSSProperties = {
	position: 'absolute',
	top: 0,
	left: 0,
	width: '100%',
	height: '100%',
	backgroundColor: 'rgba(0, 0, 0, 0.5)',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	color: '#fff',
	fontSize: 22,
	fontWeight: 'bold',
	pointerEvents: 'none',
};

interface TileProps {
	src: string;
	// paddingBottom percentage (e.g. '100%' = square, '75%' = 4:3). When omitted the
	// tile fills its parent's height (used inside a stretched grid row).
	aspect?: string;
	overlay?: number;
}

const Tile: React.FC<TileProps> = ({ src, aspect, overlay }) => {
	// HEIC/HEIF note photos (iPhone uploads) can't render in Chrome/Firefox, so
	// convert them to JPEG first — same normalization ProtectedImageThumbnails did.
	const safeSrc = useHeicSafeSrc(src);
	return (
		<div
			style={{
				position: 'relative',
				width: '100%',
				height: aspect ? undefined : '100%',
				paddingBottom: aspect,
				borderRadius: RADIUS,
				overflow: 'hidden',
				cursor: 'pointer',
			}}
		>
			<div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}>
				<Image
					src={safeSrc}
					style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
					wrapperStyle={{ width: '100%', height: '100%' }}
				/>
			</div>
			{overlay ? <div style={overlayStyle}>+{overlay}</div> : null}
		</div>
	);
};

// A photo that's part of the lightbox PreviewGroup but not shown in the collage.
// Kept in a display:none wrapper so its antd Image span leaves no empty row, and
// routed through the HEIC conversion so the lightbox can page through it too.
const HiddenImage: React.FC<{ src: string }> = ({ src }) => {
	const safeSrc = useHeicSafeSrc(src);
	return (
		<div style={{ display: 'none' }}>
			<Image src={safeSrc} />
		</div>
	);
};

interface NoteImageGridProps {
	photos: string[];
}

const NoteImageGrid: React.FC<NoteImageGridProps> = ({ photos }) => {
	if (!photos || photos.length === 0) return null;

	const count = photos.length;
	const visibleCount = Math.min(count, 4);
	const extraCount = count - visibleCount;

	// Images past the visible tiles still belong to the preview group so the lightbox
	// can page through every photo — they're just not shown in the collage.
	const hiddenImages = photos.slice(visibleCount).map((src) => <HiddenImage key={src} src={src} />);

	let collage: React.ReactNode;
	if (count === 1) {
		collage = <Tile src={photos[0]} aspect="75%" />;
	} else if (count === 2) {
		collage = (
			<div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: GAP }}>
				<Tile src={photos[0]} aspect="100%" />
				<Tile src={photos[1]} aspect="100%" />
			</div>
		);
	} else if (count === 3) {
		collage = (
			<div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: GAP }}>
				<Tile src={photos[0]} aspect="100%" />
				<div style={{ display: 'grid', gridTemplateRows: '1fr 1fr', gap: GAP }}>
					<Tile src={photos[1]} />
					<Tile src={photos[2]} />
				</div>
			</div>
		);
	} else {
		// 4+ : 2x2, with the 4th tile carrying the "+N" overlay when there are extras.
		collage = (
			<div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: GAP }}>
				<Tile src={photos[0]} aspect="100%" />
				<Tile src={photos[1]} aspect="100%" />
				<Tile src={photos[2]} aspect="100%" />
				<Tile src={photos[3]} aspect="100%" overlay={extraCount > 0 ? extraCount : undefined} />
			</div>
		);
	}

	return (
		<div
			style={{
				// A definite width so the collage lays out inside the content-sized chat
				// bubble (a percentage width would collapse to nothing here).
				width: count === 1 ? SINGLE_MAX_WIDTH : MULTI_MAX_WIDTH,
				maxWidth: '100%',
				marginTop: 4,
			}}
		>
			<Image.PreviewGroup>
				{collage}
				{hiddenImages}
			</Image.PreviewGroup>
		</div>
	);
};

export default NoteImageGrid;
